import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../redux/store";
import { logout } from "../redux/authSlice";

const Header: React.FC = () => {
  const { token } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
    setMenuOpen(false);
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const handleAnchorClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    target: string,
  ) => {
    e.preventDefault();
    closeMenu();

    // Check if we're on the landing page
    if (window.location.pathname !== "/") {
      // Navigate to landing page with the hash
      navigate(`/${target}`);
      return;
    }

    // If we're already on the landing page, scroll to the element
    const element = document.querySelector(target);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header className={scrolled ? "scrolled" : ""}>
      <div className="container">
        <div className="header-content">
          <Link to="/" className="logo" onClick={closeMenu}>
            <div className="logo-icon">
              <div className="satellite"></div>
              <div className="ping"></div>
            </div>
            <span className="logo-text">
              Space<span className="text-accent">Pings</span>
            </span>
          </Link>

          {/* Mobile menu button */}
          <div
            className={`mobile-menu-button ${menuOpen ? "active" : ""}`}
            onClick={toggleMenu}
          >
            <span></span>
            <span></span>
            <span></span>
          </div>

          <nav className={`main-nav ${menuOpen ? "open" : ""}`}>
            <ul className="menu">
              <li>
                <a
                  href="#how-it-works"
                  onClick={(e) => handleAnchorClick(e, "#how-it-works")}
                >
                  How It Works
                </a>
              </li>
              <li>
                <Link to="/pricing" onClick={closeMenu}>
                  Pricing
                </Link>
              </li>
              <li>
                <Link to="/blog" onClick={closeMenu}>
                  Spacelogs
                </Link>
              </li>
              <li>
                <Link to="/starlink" onClick={closeMenu}>
                  Starlink Command
                </Link>
              </li>
              {token ? (
                <>
                  <li>
                    <Link to="/profile" onClick={closeMenu}>
                      Profile
                    </Link>
                  </li>
                  <li>
                    <button onClick={handleLogout} className="logout-link">
                      Logout
                    </button>
                  </li>
                </>
              ) : (
                <li className="cta-item">
                  <Link
                    to="/auth?auto=true&returnTo=/checkout&action=checkout"
                    className="cosmic-button"
                    onClick={closeMenu}
                  >
                    <span className="button-text">Unlock the Cosmos</span>
                    <span className="button-icon">🚀</span>
                  </Link>
                </li>
              )}
            </ul>
          </nav>
        </div>
      </div>

      {/* Stars animation for the header */}
      <div className="header-stars">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="shooting-micro-star"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          ></div>
        ))}
      </div>
    </header>
  );
};

export default Header;
